import { track } from '@eventbrite/datalayer-library';
import { ProgressIndicator } from '@eventbrite/eds-progress-indicator';
import { getWindowObject } from '@eventbrite/feature-detection';
import { gettext } from '@eventbrite/i18n';
import { LoadingButton } from '@eventbrite/marmalade';
import loadable from '@loadable/component';
import React, { useEffect, useMemo, useState } from 'react';
import { LISTING_HEAP_IDS } from '../../../../constants';
import {
    useEventBasicInformationContext,
    useTicketContext,
} from '../../../../contexts';
import { useListingTimedEntryExperimentContext } from '../../../../contexts/ListingTimedEntryExperimentContext';
import {
    LocalizedDateTime,
    SALES_STATUS,
    UNDETERMINED_SALES_STATUSES,
    type CheckoutWidgetProps,
    type ChildEventProps,
    type ServerEvent,
} from '../../../../types';
import {
    eventIsOnSale,
    eventIsSalesEnded,
    eventIsSoldOut,
} from '../../../../utils';
import { getLocalizedFormattedPriceString } from '../../../../utils/priceFormat';
import { getFormattedDateCard } from '../../../utils/localizedDates';
import { useTimedEntryCalendarData } from '../../WhenAndWhere/DateAndTime/useTimedEntryCalendarData';
import { ExploreSimilarEventsButton } from '../ExploreSimilarEventsButton/ExploreSimilarEventsButton';
import './CheckAvailability.scss';
import { getCheckAvailabilityWidgetEventId } from './utils';

interface CheckAvailabilityProps
    extends CheckoutWidgetProps,
        Pick<ServerEvent, 'isPreview'> {
    eventStartDate?: LocalizedDateTime;
    eventEndDate?: LocalizedDateTime;
    onConfirm: () => void;
}

interface PanelDisplayMessageProps {
    locale: string;
    parentEventId?: string;
    eventStartDate?: LocalizedDateTime;
    eventEndDate?: LocalizedDateTime;
    isSoldOut?: boolean;
    isSalesEndedEvent?: boolean;
}

const getValidChildEvents = (childEvents: Array<ChildEventProps>) => {
    return childEvents.filter(
        (event) =>
            eventIsOnSale(event) ||
            UNDETERMINED_SALES_STATUSES.includes(
                event?.sales_status as SALES_STATUS,
            ) ||
            eventIsSalesEnded(event) ||
            eventIsSoldOut(event),
    );
};

const filterUnavailableEventDates = (childEvents: Array<ChildEventProps>) => {
    return childEvents.filter((event) => {
        if (!event.availability) {
            return false;
        }
        return (
            event.availability.toLowerCase() === 'soldout' ||
            event.availability.toLowerCase() === 'salesended'
        );
    });
};

const getPanelDisplayMessage = ({
    locale,
    eventStartDate,
    eventEndDate,
    parentEventId,
    isSoldOut,
    isSalesEndedEvent,
}: PanelDisplayMessageProps) => {
    if (isSalesEndedEvent) {
        return gettext('Sales Ended');
    }
    if (isSoldOut) {
        return gettext('Sold Out');
    }
    if (!locale || !eventStartDate || !eventEndDate) {
        return '';
    }

    const eventStartDateInfo = getFormattedDateCard({
        date: eventStartDate,
        locale: locale,
    });

    if (parentEventId) {
        return gettext('Multiple dates');
    } else {
        return `${eventStartDateInfo.monthAbbr} ${eventStartDateInfo.day}`;
    }
};

export const getFormattedPriceDetails = (ticketData: any, locale: string) => {
    const minimumTicketPrice = ticketData?.availability?.minimumTicketPrice;
    const eventIsFree = ticketData?.availability?.isFree;
    if (!minimumTicketPrice || eventIsFree) {
        return ticketData?.panelDisplayPrice;
    }

    const { currency, value } = minimumTicketPrice;

    if (currency == null || value == null) {
        return ticketData?.panelDisplayPrice;
    }

    const formattedPrice = getLocalizedFormattedPriceString({
        locale,
        currency,
        value: value,
    });

    return gettext('From %(price)s', {
        price: formattedPrice,
    });
};

export const CheckAvailability: React.FC<CheckAvailabilityProps> = ({
    eventStartDate,
    eventEndDate,
    onConfirm,
    isPreview,
    affiliateCode,
    campaign_token,
    rsvpToken,
    waitlistToken,
    promoCode,
}) => {
    const { isListingTimedEntryExperimentActive } =
        useListingTimedEntryExperimentContext();
    const [isLoading, setIsLoading] = useState(true);
    const basicEventInformation = useEventBasicInformationContext();
    const modalTriggerElementId = useMemo(
        () => `check-availability-btn-${basicEventInformation.parentEventId}`,
        [basicEventInformation.parentEventId],
    );
    const [moreOptionsModalOpened, setMoreOptionsModalOpened] = useState(false);
    const { ticketData, isFetchComplete } = useTicketContext();
    const { fetchTimedEntryCalendarData, loading } =
        useTimedEntryCalendarData();

    const LazyMoreOptionsModal = useMemo(
        () =>
            loadable(
                () =>
                    import(
                        '../../WhenAndWhere/ChildEventsSelection/MoreOptionsModal/MoreOptionsModal'
                    ),
            ),
        [],
    );

    useEffect(() => {
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isFetchComplete || !isListingTimedEntryExperimentActive) return;

        const eventIdToUse = getCheckAvailabilityWidgetEventId({
            waitlistToken,
            basicEventInformation,
        });

        getWindowObject('EBWidgets')?.createWidget({
            widgetType: 'checkout',
            eventId: eventIdToUse,
            modal: true,
            modalTriggerElementId,
            ...(!isPreview ? { affiliateCode } : {}),
            ...(waitlistToken ? { waitlistToken } : {}),
            ...(promoCode ? { promoCode: escape(promoCode) } : {}),
            ...(rsvpToken ? { rsvpToken } : {}),
            ...(campaign_token ? { campaign_token } : {}),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isFetchComplete,
        modalTriggerElementId,
        basicEventInformation.parentEventId,
    ]);

    if (!isFetchComplete) {
        return (
            <div className="express-checkout-progressbar">
                <ProgressIndicator style="gradient" size="medium-chunky" />
            </div>
        );
    }

    const childEventsToShow =
        basicEventInformation.currentEventSessionsTimedEntry;

    const handleMoreOptionsClick = async () => {
        setMoreOptionsModalOpened(true);
        const startDateTime =
            basicEventInformation?.start?.local ??
            childEventsToShow?.[0]?.start?.local;

        if (startDateTime) {
            await fetchTimedEntryCalendarData(startDateTime);
        }
    };

    const validChildEvents = getValidChildEvents(
        basicEventInformation.childEvents ?? [],
    );

    const unavailableEvents = filterUnavailableEventDates(
        basicEventInformation.childEvents ?? [],
    );

    const panelDisplayMessage = getPanelDisplayMessage({
        locale: basicEventInformation.locale || '',
        parentEventId: basicEventInformation.parentEventId,
        eventStartDate: basicEventInformation.parentEventId
            ? eventStartDate
            : basicEventInformation.start,
        eventEndDate: basicEventInformation.parentEventId
            ? eventEndDate
            : basicEventInformation.end,
        isSoldOut: basicEventInformation.isSoldOut,
        isSalesEndedEvent: basicEventInformation.isSalesEndedEvent,
    });

    const formattedPriceDetails = getFormattedPriceDetails(
        ticketData,
        basicEventInformation.locale || 'en-US',
    );

    const showExploreSimilarEventsButton =
        basicEventInformation.isSoldOut ||
        basicEventInformation.isSalesEndedEvent;

    return (
        <section>
            <div className="check-availability-btn check-availability-btn--checkout-opener">
                <div className="check-availability-btn__body">
                    <div className="check-availability-btn__panel-info">
                        {!showExploreSimilarEventsButton &&
                            formattedPriceDetails}
                        {panelDisplayMessage && (
                            <div
                                className={
                                    showExploreSimilarEventsButton
                                        ? 'check-availability-btn__panel-sold-out-message'
                                        : 'check-availability-btn__panel-message'
                                }
                            >
                                {panelDisplayMessage}
                            </div>
                        )}
                    </div>
                </div>
                <div className="check-availability-btn__cta-wrapper">
                    {showExploreSimilarEventsButton ? (
                        <ExploreSimilarEventsButton />
                    ) : (
                        <LoadingButton
                            id={modalTriggerElementId}
                            isLoading={isLoading}
                            disabled={isLoading}
                            onClick={() => {
                                track({
                                    eventName:
                                        LISTING_HEAP_IDS.CHECK_AVAILABILITY_CLICK,
                                    eventData: {},
                                });
                                if (isListingTimedEntryExperimentActive) {
                                    return;
                                }
                                handleMoreOptionsClick();
                            }}
                            variant="primary"
                            className="check-availability-btn__button"
                        >
                            {gettext('Check availability')}
                        </LoadingButton>
                    )}
                </div>
            </div>
            {moreOptionsModalOpened && (
                <LazyMoreOptionsModal
                    onClose={() => setMoreOptionsModalOpened(false)}
                    onConfirm={onConfirm}
                    opened={moreOptionsModalOpened}
                    childEvents={validChildEvents}
                    currentEventStartDate={basicEventInformation?.start}
                    currentEventId={basicEventInformation.id}
                    isTimedEntryEnabled={true}
                    soldOutEvents={unavailableEvents}
                    isLoading={loading}
                />
            )}
        </section>
    );
};
